import React from "react";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { Warning } from "@mui/icons-material";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

export type DOSConfirmationData = {
    reason: string;
}

export type ButtonComponentProps = {
    name: string;
    color:
    | "info"
    | "success"
    | "warning"
    | "primary"
    | "secondary"
    | "error"
    | undefined;
    onClick: (data?: DOSConfirmationData) => void;
    icon?: React.ReactNode;
    type?: "button" | "submit";
};

interface DOSConfirmationPopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: DOSConfirmationData) => void;
    title?: string;
    message: string;
    buttons?: ButtonComponentProps[];
}

const dosConfirmationInitialState: DOSConfirmationData = {
    reason: "",
};

const DOSConfirmationPopup: React.FC<DOSConfirmationPopupProps> = ({
    open,
    onClose,
    onConfirm,
    title = "Confirmation Required",
    message,
    buttons
}) => {
    const { control, handleSubmit, reset, getValues, formState: { errors } } = useForm<DOSConfirmationData>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        inputFieldSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();
    const warningHex = typeof theme.palette.warning.main === "string"
        ? theme.palette.warning.main
        : "#f59e0b";
    const ariaDescId = "dos-confirmation-dialog-description";

    React.useEffect(() => {
        if (open) {
            reset(dosConfirmationInitialState);
        }
    }, [open, reset]);

    const onSubmit = (data: DOSConfirmationData) => {
        console.log(data)
        onConfirm(data);
        reset(dosConfirmationInitialState);
        onClose();
    };

    const handleClose = () => {
        reset(dosConfirmationInitialState);
        onClose();
    };

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{ sx: paperSx(isMobile) }}
            onClick={(event) => {
                event.stopPropagation();
            }}
            aria-describedby={ariaDescId}
        >
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(warningHex)}>
                        <Warning sx={{ fontSize: 20, color: warningHex }} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4, display: "flex", alignItems: "center", minHeight: 20, pt: "12px" }}>
                        <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.2 }}>
                            {title}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        ...bodySx,
                        minHeight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                        scrollbarGutter: "stable",
                        "&::-webkit-scrollbar": { width: 6, height: 6 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                            borderRadius: 4,
                        },
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography id={ariaDescId} variant="body1" sx={{ fontSize: "0.9rem", color: tokens.textSecondary, mb: 2 }}>
                                {message}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FieldLabel label="Reason for Change" required tokens={tokens} />
                            <Box sx={inputFieldSx(!!errors.reason)}>
                                <CustomTextField
                                    control={control}
                                    controllerName="reason"
                                    label=""
                                    multiline
                                    rows={3}
                                    rules={{
                                        required: "Reason is required.",
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={footerSx}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        {buttons ? (
                            buttons.map((button, index) =>
                                button.type === "submit" ? (
                                    <Box
                                        key={index}
                                        component="button"
                                        type="submit"
                                        sx={{
                                            ...primaryBtnSx,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.8,
                                            px: 3,
                                            py: 1.2,
                                            border: "none",
                                            cursor: "pointer",
                                            color: "#fff",
                                            order: { xs: 1, sm: 2 },
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                    >
                                        {button.icon}
                                        {button.name}
                                    </Box>
                                ) : (
                                    <Box
                                        key={index}
                                        component="button"
                                        type="button"
                                        onClick={() => button.onClick()}
                                        sx={{
                                            ...cancelBtnSx,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.8,
                                            px: 3,
                                            py: 1.2,
                                            border: "1px solid",
                                            cursor: "pointer",
                                            order: { xs: 2, sm: 1 },
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                    >
                                        {button.icon}
                                        {button.name}
                                    </Box>
                                )
                            )
                        ) : (
                            <>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => (onConfirm ? onConfirm(getValues()) : onClose())}
                                    sx={{
                                        ...primaryBtnSx,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.8,
                                        px: 3,
                                        py: 1.2,
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#fff",
                                        order: { xs: 1, sm: 2 },
                                        width: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    OK
                                </Box>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => onClose()}
                                    sx={{
                                        ...cancelBtnSx,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.8,
                                        px: 3,
                                        py: 1.2,
                                        border: "1px solid",
                                        cursor: "pointer",
                                        order: { xs: 2, sm: 1 },
                                        width: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    Cancel
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default DOSConfirmationPopup;
